import { useState } from "react";
import {
  useForm,
  type FieldErrors,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormRegister,
  type UseFormWatch,
} from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import wooCustomApiService from "@/api/services/wooCustomApiService";
import { transformOrderTotals } from "@/helpers/helpers";
import { extractErrorMessage } from "@/utils/error";
import type { OrderDetails } from "@/types/order";

//#region type
interface OrderPageViewModel {
  order: OrderDetails | null;
  isSubmitting: boolean;
  orderResultError: string | null;
  errors: FieldErrors<OrderQueryForm>;
  register: UseFormRegister<FormSchemaType>;
  watch: UseFormWatch<FormSchemaType>;
  handleFormSubmit: () => void;
}

interface OrderQueryForm {
  orderId: number | undefined;
  email: string;
}

type FormSchemaType = z.infer<typeof formSchema>;
//#endregion type

const defaultFormValues = {
  orderId: undefined,
  email: "",
};

const formSchema = z.object({
  orderId: z.coerce.number().positive("請輸入正確的訂單編號"),
  email: z.string().email("Email 格式錯誤"),
});

const useOrderPage = (): OrderPageViewModel => {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [orderResultError, setOrderResultError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const handleSubmitSuccess: SubmitHandler<FormSchemaType> = async (form) => {
    setOrderResultError(null);
    setOrder(null);
    setIsSubmitting(true);

    try {
      const { data: orderDetails } = await wooCustomApiService.order.getOrderDetails(form.orderId, {
        billing_email: form.email,
      });
      setOrder({
        ...orderDetails,
        totals: {
          ...orderDetails.totals,
          ...transformOrderTotals(orderDetails.totals),
        },
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(error, "找不到對應的訂單，請確認輸入的資料");
      setOrderResultError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitError: SubmitErrorHandler<FormSchemaType> = () => {};

  const handleFormSubmit = handleSubmit(handleSubmitSuccess, handleSubmitError);

  return {
    order,
    isSubmitting,
    orderResultError,
    errors,
    register,
    watch,
    handleFormSubmit,
  };
};

export default useOrderPage;
